import { useEffect, useCallback, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { useSafeSearchParams } from './useSafeSearchParams';
import { useSession } from 'next-auth/react';
import { v4 as uuidv4 } from 'uuid';
import { EventType, EventData, GTMEventData } from '@/api/types/events';
import { useLatestPayloadStore } from '@/stores/useLatestPayloadStore';
import { sendGTMEvent as nextSendGTMEvent } from '@next/third-parties/google';
import { captureAttributionFromBrowser, mergeEventWithStoredAttribution } from '@/lib/analytics/attribution';

// Add this at the module level, outside any component or hook
// Use module-level variable to track across hook instances and renders
const trackedPageTransitions = new Set<string>();

// Helper to get session ID from localStorage or create new one
const getSessionId = () => {
    if (typeof window === 'undefined') return undefined;

    let sessionId = localStorage.getItem('tracking_session_id');
    if (!sessionId) {
        sessionId = uuidv4();
        localStorage.setItem('tracking_session_id', sessionId);
    }
    return sessionId;
};


// Send event to Google Tag Manager using the imported sendGTMEvent function
const sendGTMEvent = (data: GTMEventData) => {
    if (typeof window !== 'undefined') {
        // Use the imported nextSendGTMEvent function
        nextSendGTMEvent(data);
    }
};

// Map our event type to GTM event category
const mapEventTypeToCategory = (eventType: EventType): string => {
    const map: Record<EventType, string> = {
        [EventType.PAGE_VIEW]: 'page_engagement',
        [EventType.CLICK]: 'user_engagement',
        [EventType.SCROLL]: 'user_engagement',
        [EventType.UNSUBSCRIBE]: 'user_engagement',
        [EventType.SEARCH]: 'search',
        [EventType.FILTER]: 'search',
        [EventType.REDIRECT]: 'navigation',
        [EventType.SORT]: 'search',
        [EventType.SUBMIT_FORM]: 'form',
        [EventType.DOWNLOAD]: 'file',
        [EventType.UPLOAD]: 'file',
        [EventType.JOB_VIEW]: 'job_engagement',
        [EventType.JOB_APPLY]: 'job_conversion',
        [EventType.LOGIN]: 'authentication',
        [EventType.LOGOUT]: 'authentication',
        [EventType.SIGNUP]: 'authentication',
        [EventType.PROFILE_UPDATE]: 'user',
        [EventType.ERROR]: 'error',
        [EventType.FEATURE_TOGGLE]: 'feature',
        [EventType.PERFORMANCE_METRIC]: 'performance',
        [EventType.CONVERSION]: 'conversion',
        [EventType.NAVIGATION]: 'navigation',
        [EventType.DIALOG_OPEN]: 'user_engagement',
        [EventType.DIALOG_CLOSE]: 'user_engagement',
        [EventType.FEEDBACK]: 'feedback'
    };

    return map[eventType] || 'other';
};

// Map internal event types to GA4 event names.
// Use GA4 recommended names where they are a clear semantic fit.
const mapEventTypeToGA4EventName = (eventType: EventType): string => {
    const map: Partial<Record<EventType, string>> = {
        [EventType.PAGE_VIEW]: 'page_view',
        [EventType.SCROLL]: 'scroll',
        [EventType.SEARCH]: 'search',
        [EventType.LOGIN]: 'login',
        [EventType.SIGNUP]: 'sign_up',
        [EventType.DOWNLOAD]: 'file_download'
    };

    return map[eventType] || eventType.toLowerCase();
};

// Convert our event to GTM format
const convertToGTMEvent = (event: EventData): GTMEventData => {
    const category = mapEventTypeToCategory(event.event_type);
    const details = event.event_details || {};
    const detailUtmParams = (details.utm_params as Record<string, string> | undefined) || {};
    const detailClickIds = (details.click_ids as Record<string, string> | undefined) || {};

    const gtmEvent: GTMEventData = {
        event: mapEventTypeToGA4EventName(event.event_type),
        event_category: category,
        event_action: event.event_type,
        event_label: event.element_id || event.page_path,
        event_value: event.interaction_value,
        page_path: event.page_path,
        page_location: event.url,
        page_title: typeof event.event_details?.page_title === 'string' ? event.event_details.page_title : undefined,
        user_id: event.user_id,
        session_id: event.session_id
    };

    // Add UTM parameters if available
    gtmEvent.utm_source = event.utm_source || detailUtmParams.utm_source;
    gtmEvent.utm_medium = event.utm_medium || detailUtmParams.utm_medium;
    gtmEvent.utm_campaign = event.utm_campaign || detailUtmParams.utm_campaign;
    gtmEvent.utm_content = event.utm_content || detailUtmParams.utm_content;
    gtmEvent.utm_term = event.utm_term || detailUtmParams.utm_term;

    if (!gtmEvent.utm_source) delete gtmEvent.utm_source;
    if (!gtmEvent.utm_medium) delete gtmEvent.utm_medium;
    if (!gtmEvent.utm_campaign) delete gtmEvent.utm_campaign;
    if (!gtmEvent.utm_content) delete gtmEvent.utm_content;
    if (!gtmEvent.utm_term) delete gtmEvent.utm_term;

    if (detailClickIds.gclid) gtmEvent.gclid = detailClickIds.gclid;
    if (detailClickIds.fbclid) gtmEvent.fbclid = detailClickIds.fbclid;
    if (detailClickIds.msclkid) gtmEvent.msclkid = detailClickIds.msclkid;
    if (detailClickIds.ttclid) gtmEvent.ttclid = detailClickIds.ttclid;
    if (detailClickIds.twclid) gtmEvent.twclid = detailClickIds.twclid;
    if (detailClickIds.li_fat_id) gtmEvent.li_fat_id = detailClickIds.li_fat_id;

    // Add any custom properties from event_details
    if (event.event_details) {
        Object.entries(event.event_details).forEach(([key, value]) => {
            gtmEvent[key] = value;
        });
    }

    return gtmEvent;
};

// Send event to the backend API
const sendEventToAPI = async (eventData: EventData) => {
    try {
        // Client-side tracking through API
        const response = await fetch('/api/events', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ events: eventData }),
            // Don't block navigation with this request
            keepalive: true
        });

        if (!response.ok) {
            console.error('Failed to track event:', await response.json());
        }

        return response.ok;
    } catch (error) {
        console.error('Error sending event to API:', error);
        return false;
    }
};

interface UseEventTrackingOptions {
    autoTrackPageViews?: boolean;
    autoTrackScrollDepth?: boolean;
    onEventTracked?: (eventData: EventData) => void;
}

/**
 * Hook for tracking events both to the backend and GTM.
 * Compatible with both Next.js Pages Router and App Router.
 */
export const useEventTracking = (options: UseEventTrackingOptions = {}) => {
    const {
        autoTrackPageViews = false,
        autoTrackScrollDepth = false,
        onEventTracked = () => { }
    } = options;

    // Use App Router hooks exclusively
    const pathname = usePathname();
    const searchParams = useSafeSearchParams();

    const { data: session } = useSession();
    const sessionIdRef = useRef<string | undefined>(undefined);
    const previousPathRef = useRef<string | undefined>(undefined);
    const maxScrollRef = useRef<number>(0);
    const { getLatestPayload } = useLatestPayloadStore();

    // Initialize session ID
    useEffect(() => {
        sessionIdRef.current = getSessionId();
    }, []);

    // Create stable version of trackEvent that doesn't change on every render
    const stableTrackEvent = useCallback(async (eventData: Partial<EventData>, onEventTracked?: (eventData: EventData) => void) => {
        try {
            // Ensure required fields are present
            if (!eventData.event_type) {
                console.error('Event type is required');
                return;
            }
            const { payload } = getLatestPayload();

            // Construct path from App Router hooks
            const search = searchParams ? searchParams.toString() : '';
            // Ensure pathname is not null before constructing path
            const basePath = pathname ?? ''; // Use empty string if pathname is null
            const fullPath = search ? `${basePath}?${search}` : basePath;
            const currentPath = eventData.page_path || fullPath;

            // Create complete event data
            const completeEventData: EventData = {
                event_type: eventData.event_type,
                session_id: sessionIdRef.current,
                user_id: session?.user?.id,
                email: session?.user?.email as string | undefined,
                page_path: currentPath,
                url: typeof window !== 'undefined' ? window.location.href : undefined,
                referrer: typeof document !== 'undefined' ? document.referrer : undefined,
                user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
                timestamp: Math.floor(Date.now() / 1000),
                payload: payload || undefined,
                ...eventData,
                event_details: {
                    page_title: typeof document !== 'undefined' ? document.title : undefined,
                    ...(eventData.event_details || {})
                }
            };

            // Capture first/last-touch attribution and extract current UTM parameters.
            captureAttributionFromBrowser();
            if (typeof window !== 'undefined' && window.location.search) {
                const searchParams = new URLSearchParams(window.location.search);
                if (!completeEventData.utm_source) {
                    const value = searchParams.get('utm_source');
                    if (value) completeEventData.utm_source = value;
                }
                if (!completeEventData.utm_medium) {
                    const value = searchParams.get('utm_medium');
                    if (value) completeEventData.utm_medium = value;
                }
                if (!completeEventData.utm_campaign) {
                    const value = searchParams.get('utm_campaign');
                    if (value) completeEventData.utm_campaign = value;
                }
                if (!completeEventData.utm_content) {
                    const value = searchParams.get('utm_content');
                    if (value) completeEventData.utm_content = value;
                }
                if (!completeEventData.utm_term) {
                    const value = searchParams.get('utm_term');
                    if (value) completeEventData.utm_term = value;
                }

                searchParams.forEach((value, key) => {
                    if (!key.toLowerCase().startsWith('utm_')) return;
                    completeEventData.event_details = {
                        ...(completeEventData.event_details || {}),
                        utm_params: {
                            ...((completeEventData.event_details?.utm_params as Record<string, string>) || {}),
                            [key.toLowerCase()]: value
                        }
                    };
                });
            }

            const shouldPreferFirstTouch =
                completeEventData.event_type === EventType.LOGIN ||
                completeEventData.event_type === EventType.SIGNUP ||
                completeEventData.event_type === EventType.CONVERSION;

            const enrichedEventData = mergeEventWithStoredAttribution(completeEventData, {
                preferFirstTouch: shouldPreferFirstTouch
            });

            // Push to GTM
            const gtmEvent = convertToGTMEvent(enrichedEventData);
            sendGTMEvent(gtmEvent);

            // Save to API
            await sendEventToAPI(enrichedEventData);

            onEventTracked?.(enrichedEventData);
        } catch (error) {
            console.error('Error tracking event:', error);
        }
    }, [getLatestPayload, session, pathname, searchParams]); // Include session, pathname and searchParams in dependencies

    // Auto-track page views
    useEffect(() => {
        if (!autoTrackPageViews || typeof window === 'undefined' || pathname === null) return; // Also check pathname null

        // Get the full path including any relevant query parameters
        const search = searchParams ? searchParams.toString() : '';
        const fullPath = search ? `${pathname}?${search}` : pathname;

        // Initialize previousPathRef if it's undefined (first render)
        if (previousPathRef.current === undefined) {
            previousPathRef.current = fullPath;
            // Skip tracking on initial render, just set the path
        }

        // Create a unique key for this path transition to avoid duplicate tracking
        const transitionKey = `${previousPathRef?.current || ''}_to_${fullPath}`;

        if (fullPath &&
            (previousPathRef.current !== fullPath
                || trackedPageTransitions?.size === 0
            ) && !trackedPageTransitions?.has(transitionKey)) {

            // Mark this transition as processed at module level for persistence
            trackedPageTransitions.add(transitionKey);

            stableTrackEvent({
                event_type: EventType.PAGE_VIEW,
                page_path: fullPath
            }, onEventTracked);
            maxScrollRef.current = 0;
            previousPathRef.current = fullPath;
        }

        // Cleanup function to limit set size
        return () => {
            // Keep the set from growing too large
            if (trackedPageTransitions.size > 100) {
                // Keep the 50 most recent transitions
                const entries = Array.from(trackedPageTransitions);
                trackedPageTransitions.clear();
                entries.slice(-50).forEach(entry => trackedPageTransitions.add(entry));
            }
        };
    }, [autoTrackPageViews, stableTrackEvent, pathname, searchParams, onEventTracked]);

    // Auto-track scroll depth
    useEffect(() => {
        if (!autoTrackScrollDepth || typeof window === 'undefined' || pathname === null) return; // Also check pathname null

        const handleScroll = () => {
            const scrollTop = window.scrollY || document.documentElement.scrollTop;
            const scrollHeight = document.documentElement.scrollHeight;
            const clientHeight = document.documentElement.clientHeight;

            // Calculate scroll percentage
            const scrollPercentage = Math.round((scrollTop / (scrollHeight - clientHeight)) * 100);

            // Track at 25%, 50%, 75%, 90% and 100% thresholds if not already tracked
            const thresholds = [25, 50, 75, 90, 100];
            const search = searchParams ? searchParams.toString() : '';
            // Ensure pathname is not null before constructing path
            const basePath = pathname ?? ''; // Use empty string if pathname is null
            const currentPathForScroll = search ? `${basePath}?${search}` : basePath;

            thresholds.forEach(threshold => {
                if (scrollPercentage >= threshold && maxScrollRef.current < threshold) {
                    stableTrackEvent({
                        event_type: EventType.SCROLL,
                        interaction_value: threshold,
                        event_details: {
                            scroll_depth: threshold,
                            page: currentPathForScroll
                        }
                    }, onEventTracked);
                    maxScrollRef.current = threshold;
                }
            });
        };

        // Debounce scroll handler
        let scrollTimeout: NodeJS.Timeout | null = null;
        const debouncedScrollHandler = () => {
            if (scrollTimeout) clearTimeout(scrollTimeout);
            scrollTimeout = setTimeout(handleScroll, 150);
        };

        window.addEventListener('scroll', debouncedScrollHandler, { passive: true });

        // Clear tracked scroll events on unmount or path change
        const cleanupScrollTracking = () => {
            window.removeEventListener('scroll', debouncedScrollHandler);
            if (scrollTimeout) clearTimeout(scrollTimeout);
            // Clear flags when component unmounts or path changes substantially
            const thresholds = [25, 50, 75, 90, 100];
            const search = searchParams ? searchParams.toString() : '';
            // Ensure pathname is not null before constructing path
            const basePath = pathname ?? ''; // Use empty string if pathname is null
            const currentPathForCleanup = search ? `${basePath}?${search}` : basePath;
            thresholds.forEach(threshold => {
                const eventKey = `scroll_${threshold}_${currentPathForCleanup}`;
                delete window[eventKey];
            });
        };

        return cleanupScrollTracking;

    }, [autoTrackScrollDepth, stableTrackEvent, pathname, searchParams, onEventTracked]);

    return { trackEvent: stableTrackEvent };
};

// Extend window type for GTM function and scroll tracking flags
declare global {
    interface Window {
        [key: `scroll_${number}_${string}`]: boolean | undefined;
    }
} 
